import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useMutation } from '@apollo/client';

import { ErrorHandler, ConfirmDialog as ModalDelete } from '@playbookux/playbook-web-core';
import withLayout from '../../../core/components/with-layout';
import ModalTagGroup from './modal-tag-group/index';
import ModalMergeTagGroup from './modal-tag-group/merge-tag-group';
import ModalTag from './modal-tag/index';
import ModalMergeTag from './modal-tag/merge-tag';
import tagType from '../../../core/constants/tagType';
import TagGroupsTable from './tag-groups-table';
import useTags from '../../../core/hooks/use-tags';
import {
  DELETE_TAG_GROUP,
  DELETE_TAG,
} from '../../../core/graphql/tags';

import {
  Container,
  HorizontalContent,
  ButtonTagGroup,
  Text,
  Link,
  ContainerText,
  Loader,
} from './styles';

const ManageTags = (props) => {
  const {
    companyId,
  } = props;

  const LEARN_MORE_LINK = 'http://help.playbookux.com/en/articles/4636329-tags-coding-qualitative-insights';

  const [editTagGroup, setEditTagGroup] = useState({});
  const [editTag, setEditTag] = useState({});
  const [mergeTag, setMergeTag] = useState({});
  const [mergeTagGroup, setMergeTagGroup] = useState({});
  const [selectedTagGroup, setSelectedTagGroup] = useState('');
  const [isTagGrouModalpOpen, setIsTagGrouModalpOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isMergeTagModalOpen, setIsMergeTagModalOpen] = useState(false);
  const [isMergeTagGroupModalOpen, setIsMergeTagGroupModalOpen] = useState(false);
  const [isModalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [tag, setTag] = useState(null);

  const handleOpenTagGroupModal = (tagGroup) => {
    setEditTagGroup(tagGroup);
    setIsTagGrouModalpOpen(true);
  };

  const handleOpenTagGroupMergeModal = (tagGroup) => {
    setMergeTagGroup(tagGroup);
    setIsMergeTagGroupModalOpen(true);
  };

  const handleCloseGroupModal = () => setIsTagGrouModalpOpen(false);

  const handleOpenCreateTagModal = (tagGroup) => {
    setSelectedTagGroup(tagGroup);
    setIsTagModalOpen(true);
  };
  const handleOpenEditTagModal = (tagGroup, currentTag) => {
    setSelectedTagGroup(tagGroup);
    setEditTag(currentTag);
    setIsTagModalOpen(true);
  };

  const handleOpenMergeModal = (tagGroup, currentTag) => {
    setSelectedTagGroup(tagGroup);
    setMergeTag(currentTag);
    setIsMergeTagModalOpen(true);
  };

  const handleCloseTagModal = () => setIsTagModalOpen(false);
  const handleCloseMergeTagModal = () => setIsMergeTagModalOpen(false);
  const handleCloseMergeTagGroupModal = () => setIsMergeTagGroupModalOpen(false);

  const {
    groupedTags,
    loading,
    error,
    refetch,
  } = useTags(companyId) || {};

  const [deleteTagGroup, {
    loading: isLoading,
    error: hasError,
  }] = useMutation(DELETE_TAG_GROUP);

  const [deleteTag, {
    loading: isLoadingTag,
    error: hasErrorTag,
  }] = useMutation(DELETE_TAG);

  const handleOpenModalDelete = (id, type) => {
    setTag({ id, type });
    setModalDeleteOpen(true);
  };

  const handleCloseModalDelete = () => {
    setTag(null);
    setModalDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (tag && tag.type === tagType.TAG_GROUP) {
      await deleteTagGroup(
        {
          variables: {
            id: tag.id,
          },
        },
      );
    }

    if (tag && tag.type === tagType.TAG) {
      await deleteTag(
        {
          variables: {
            id: tag.id,
          },
        },
      );
    }
    refetch();
    handleCloseModalDelete();
  };

  if (error) {
    return (
      <Container center>
        <ErrorHandler
          error={error}
          defaultMessage="Error while retrieving tags."
        />
      </Container>
    );
  }

  return (
    <>
      <Container>
        <HorizontalContent>
          <ContainerText>
            <Text>Manage Tags</Text>
            <Link
              href={LEARN_MORE_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about tagging
            </Link>
          </ContainerText>

          {loading && <Loader data-loader="manage-tags-loader" />}
          <ButtonTagGroup
            onClick={() => handleOpenTagGroupModal({})}
          >
            Create Tag Group
          </ButtonTagGroup>
        </HorizontalContent>

        <TagGroupsTable
          onCreateTagClick={(tagGroup) => handleOpenCreateTagModal(tagGroup)}
          currentCompany={companyId}
          groupedTags={groupedTags}
          handleOpenModalDelete={handleOpenModalDelete}
          handleOpenTagGroupModal={(row) => handleOpenTagGroupModal(row)}
          handleOpenEditTagModal={(tagGroup, currentTag) => handleOpenEditTagModal(tagGroup, currentTag)}
          handleMergeModal={(tagGroup, currentTag) => handleOpenMergeModal(tagGroup, currentTag)}
          handleMergeGroupModal={(tagGroup) => handleOpenTagGroupMergeModal(tagGroup)}
        />
      </Container>
      {isTagGrouModalpOpen && (
        <ModalTagGroup
          editTagGroup={editTagGroup}
          onClose={handleCloseGroupModal}
          companyId={companyId}
        />
      )}
      {isTagModalOpen && (
        <ModalTag
          companyId={companyId}
          defaultTagGroup={selectedTagGroup}
          editTag={editTag}
          onClose={handleCloseTagModal}
          tagGroups={groupedTags}
          setEditTag={setEditTag}
        />
      )}
      {isMergeTagModalOpen && (
        <ModalMergeTag
          companyId={companyId}
          defaultTagGroup={selectedTagGroup}
          mergeTag={mergeTag}
          onClose={handleCloseMergeTagModal}
          groupedTags={groupedTags}
          setMergeTag={setEditTag}
          refetch={() => refetch()}
        />
      )}
      {isMergeTagGroupModalOpen && (
        <ModalMergeTagGroup
          companyId={companyId}
          defaultTagGroup={selectedTagGroup}
          mergeTag={mergeTagGroup}
          onClose={handleCloseMergeTagGroupModal}
          groupedTags={groupedTags}
          setMergeTag={setEditTag}
          refetch={() => refetch()}
        />
      )}
      {isModalDeleteOpen && (
        <ModalDelete
          show
          title="Delete tag"
          warning="Warning: This cannot be undone"
          confirmText="Yes, delete tag"
          cancelText="Cancel, keep tag"
          handleClose={handleCloseModalDelete}
          handleConfirm={handleConfirmDelete}
          errorMessage={(hasError || hasErrorTag) && 'Something went wrong while deleting'}
          loading={isLoading || isLoadingTag}
        />
      )}
    </>
  );
};

ManageTags.propTypes = {
  companyId: PropTypes.string.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  withLayout({ selectedTab: null }),
  connect(mapStateToProps, null),
);

export default enhance(ManageTags);
